import type { ReactNode } from "react"
import { Check } from "lucide-react"
import { cn } from "@/lib/utils"
import { ACCENT_STYLES } from "./pricing-plan-cards"

interface PricingPlanCardProps {
  planId?: string
  headingId: string
  headingLevel?: "h2" | "h3"
  title: string
  description: string
  icon: ReactNode
  accent: string
  featured?: boolean
  badge?: ReactNode
  status?: ReactNode
  pricing: ReactNode
  featureHighlight?: ReactNode
  features: readonly string[]
  actions: ReactNode
  footer?: ReactNode
}

// Shared presentation only: callers retain their own pricing and checkout logic.
export function PricingPlanCard({
  planId,
  headingId,
  headingLevel: Heading = "h3",
  title,
  description,
  icon,
  accent: accentName,
  featured = false,
  badge,
  status,
  pricing,
  featureHighlight,
  features,
  actions,
  footer,
}: PricingPlanCardProps) {
  const accent = ACCENT_STYLES[accentName] ?? ACCENT_STYLES.blue

  return (
    <section
      data-plan={planId}
      aria-labelledby={headingId}
      className={cn(
        "group relative rounded-3xl transition-all duration-300",
        "bg-white/80 dark:bg-black/40 backdrop-blur-md border",
        featured
          ? "border-primary/50 shadow-2xl shadow-primary/10 md:scale-[1.02] z-10"
          : "border-zinc-200 dark:border-white/10 hover:border-primary/30 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-1"
      )}
    >
      <div
        aria-hidden="true"
        className={cn(
          "absolute inset-0 rounded-3xl bg-gradient-to-b opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none",
          accent.gradient
        )}
      />
      {badge ? (
        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-20">{badge}</div>
      ) : null}
      <div className="relative z-10 flex h-full flex-col p-6">
        {status ? <div className="absolute right-3 top-3">{status}</div> : null}
        <div className="mb-5">
          <div
            aria-hidden="true"
            className={cn(
              "mb-4 inline-flex h-11 w-11 items-center justify-center rounded-2xl transition-transform duration-300 group-hover:scale-105",
              accent.iconBg
            )}
          >
            <div className={accent.iconColor}>{icon}</div>
          </div>
          <Heading
            id={headingId}
            className="mb-1.5 text-xl font-bold text-zinc-900 dark:text-white"
          >
            {title}
          </Heading>
          <p className="text-zinc-500 dark:text-zinc-400 text-sm leading-relaxed min-h-[2.5rem]">
            {description}
          </p>
        </div>
        <div className="mb-5 rounded-2xl border border-zinc-100 bg-zinc-50 p-3 dark:border-white/5 dark:bg-zinc-900/50">
          {pricing}
        </div>
        <div className="mb-6 flex-grow space-y-3">
          {featureHighlight}
          <ul className="space-y-3">
            {features.map((feature) => (
              <li key={feature} className="group/feature flex items-start gap-2.5">
                <span
                  aria-hidden="true"
                  className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-green-500/10 transition-colors group-hover/feature:bg-green-500/20"
                >
                  <Check className="h-2.5 w-2.5 text-green-600 dark:text-green-400" />
                </span>
                <span className="text-[13px] leading-5 text-zinc-700 transition-colors group-hover/feature:text-zinc-900 dark:text-zinc-300 dark:group-hover/feature:text-white">
                  {feature}
                </span>
              </li>
            ))}
          </ul>
        </div>
        {actions}
        {footer}
      </div>
    </section>
  )
}
